import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

export default function Employees() {
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', hourly_rate: '', password: '' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [updatingRole, setUpdatingRole] = useState(null)
  const [resetTarget, setResetTarget] = useState(null)
  const [filter, setFilter] = useState('active')

  useEffect(() => { fetchEmployees() }, [])

  async function fetchEmployees() {
    const { data } = await supabase.from('profiles').select('*, time_entries(id, clock_in, clock_out)').order('full_name')
    setEmployees(data || [])
    setLoading(false)
  }

  async function setActive(emp, active) {
    if (!active && !window.confirm(`Deactivate ${emp.full_name}? They'll stop appearing as an option when assigning jobs, schedules, or site visits, and their time entries/job history stay intact. This does NOT block their login or remove app access -- for that, change their password or contact Matt to remove their account. You can reactivate them anytime.`)) return
    const { error } = await supabase.from('profiles').update({ active }).eq('id', emp.id)
    if (error) { alert('Failed to update: ' + error.message); return }
    setEmployees(emps => emps.map(e => e.id === emp.id ? { ...e, active } : e))
  }

  async function handleAdd(e) {
    e.preventDefault()
    setSaving(true)
    setError('')

    if (!form.full_name.trim()) { setError('Full name is required'); setSaving(false); return }
    if (!form.password || form.password.length < 6) { setError('Password must be at least 6 characters'); setSaving(false); return }

    const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
    if (sessionError || !sessionData?.session) {
      setError('Session expired. Please sign out and sign back in.')
      setSaving(false)
      return
    }

    let result
    try {
      const res = await fetch(`https://azkrlieifdqhjnghavkg.supabase.co/functions/v1/create-employee`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.session.access_token}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          full_name: form.full_name.trim(),
          email: form.email.trim() || '',
          phone: form.phone.trim(),
          hourly_rate: form.hourly_rate || '0',
          password: form.password
        })
      })

      // Check HTTP status first
      if (!res.ok) {
        const text = await res.text()
        setError(`Server error (${res.status}): ${text}`)
        setSaving(false)
        return
      }

      result = await res.json()
    } catch (fetchError) {
      setError('Network error: ' + fetchError.message)
      setSaving(false)
      return
    }

    if (result.error) {
      setError(result.error)
      setSaving(false)
      return
    }

    // Success
    setShowAdd(false)
    setForm({ full_name: '', email: '', phone: '', hourly_rate: '', password: '' })
    fetchEmployees()
    setSaving(false)
  }

  async function updateRole(emp, newRole) {
    setUpdatingRole(emp.id)
    await supabase.from('profiles').update({ role: newRole }).eq('id', emp.id)
    setEmployees(emps => emps.map(e => e.id === emp.id ? { ...e, role: newRole } : e))
    setUpdatingRole(null)
  }

  async function updateRate(emp, rate) {
    await supabase.from('profiles').update({ hourly_rate: parseFloat(rate) || 0 }).eq('id', emp.id)
    setEmployees(emps => emps.map(e => e.id === emp.id ? { ...e, hourly_rate: rate } : e))
  }

  function totalHours(emp) {
    return (emp.time_entries || []).reduce((sum, t) => {
      if (!t.clock_out) return sum
      return sum + (new Date(t.clock_out) - new Date(t.clock_in)) / 3600000
    }, 0).toFixed(1)
  }

  function isClockedIn(emp) {
    return (emp.time_entries || []).some(t => !t.clock_out)
  }

  const inviteLink = `${window.location.origin}/signup`
  const visibleEmployees = employees.filter(emp =>
    filter === 'all' || (filter === 'active' ? emp.active !== false : emp.active === false)
  )

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Employees</h1>
          <p style={styles.sub}>{employees.filter(e => e.active !== false).length} active · {employees.filter(e => e.active === false).length} inactive</p>
        </div>
        <button style={styles.addBtn} onClick={() => { setShowAdd(!showAdd); setError('') }}>+ Add Employee</button>
      </div>

      <div style={styles.filterBar}>
        {['active', 'inactive', 'all'].map(f => (
          <button key={f} style={filter === f ? styles.filterBtnActive : styles.filterBtn} onClick={() => setFilter(f)}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div style={styles.inviteBox}>
        <div style={styles.inviteLeft}>
          <span style={styles.inviteIcon}>🔗</span>
          <div>
            <div style={styles.inviteTitle}>Employee Signup Link — send this to new team members</div>
            <div style={styles.inviteUrl}>{inviteLink}</div>
          </div>
        </div>
        <button style={styles.copyBtn} onClick={() => { navigator.clipboard.writeText(inviteLink); alert('Link copied!') }}>
          Copy Link
        </button>
      </div>

      {showAdd && (
        <div style={styles.addCard}>
          <h2 style={styles.cardTitle}>Add Employee Manually</h2>
          <p style={styles.cardSub}>Creates a login account. Share the password with them so they can sign in.</p>
          <form onSubmit={handleAdd}>
            {error && <div style={styles.error}>{error}</div>}
            <div style={styles.formGrid}>
              <div>
                <label style={styles.label}>Full Name *</label>
                <input style={styles.input} value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} required />
              </div>
              <div>
                <label style={styles.label}>Email <span style={styles.optional}>(optional)</span></label>
                <input style={styles.input} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div>
                <label style={styles.label}>Phone</label>
                <input style={styles.input} type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
              <div>
                <label style={styles.label}>Hourly Rate ($)</label>
                <input style={styles.input} type="number" step="0.01" value={form.hourly_rate} onChange={e => setForm(f => ({ ...f, hourly_rate: e.target.value }))} />
              </div>
              <div>
                <label style={styles.label}>Password * <span style={styles.optional}>(they'll use this to sign in)</span></label>
                <input style={styles.input} type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Min 6 characters" required />
              </div>
            </div>
            <div style={styles.formActions}>
              <button type="button" style={styles.cancelBtn} onClick={() => { setShowAdd(false); setError('') }}>Cancel</button>
              <button type="submit" style={styles.saveBtn} disabled={saving}>{saving ? 'Creating...' : 'Add Employee'}</button>
            </div>
          </form>
        </div>
      )}

      {loading ? <div style={styles.loading}>Loading...</div> : (
        <div style={styles.list}>
          {visibleEmployees.map(emp => {
            const isInactive = emp.active === false
            return (
              <div key={emp.id} style={{ ...styles.card, opacity: isInactive ? 0.6 : 1 }}>
                <div style={styles.avatar}>{(emp.full_name || emp.email || '?').charAt(0).toUpperCase()}</div>
                <div style={styles.info}>
                  <div style={styles.nameRow}>
                    <span style={styles.name}>{emp.full_name || emp.email}</span>
                    {isInactive && <span style={styles.inactiveBadge}>Inactive</span>}
                    {!isInactive && isClockedIn(emp) && <div style={styles.clockedIn}>⏱️ Clocked In</div>}
                  </div>
                  <div style={styles.details}>
                    {emp.email && <span>{emp.email}</span>}
                    {emp.phone && <span>📞 {emp.phone}</span>}
                    <span>⏰ {totalHours(emp)}h total</span>
                  </div>
                </div>
                <div style={styles.right}>
                  <div style={styles.rateRow}>
                    <span style={styles.rateLabel}>$/hr</span>
                    <input style={styles.rateInput} type="number" step="0.01" value={emp.hourly_rate || ''} onChange={e => updateRate(emp, e.target.value)} placeholder="0" />
                  </div>
                  <div style={styles.roleRow}>
                    <button style={{ ...styles.roleBtn, ...(emp.role === 'employee' ? styles.roleBtnActive : styles.roleBtnInactive) }} onClick={() => updateRole(emp, 'employee')} disabled={updatingRole === emp.id}>Employee</button>
                    <button style={{ ...styles.roleBtn, ...(emp.role === 'admin' ? styles.roleBtnAdmin : styles.roleBtnInactive) }} onClick={() => updateRole(emp, 'admin')} disabled={updatingRole === emp.id}>Admin</button>
                    <button style={{ ...styles.roleBtn, ...styles.roleBtnReset }} onClick={() => setResetTarget(emp)}>🔑 Password Help</button>
                    {isInactive
                      ? <button style={{ ...styles.roleBtn, ...styles.roleBtnReactivate }} onClick={() => setActive(emp, true)}>Reactivate</button>
                      : <button style={{ ...styles.roleBtn, ...styles.roleBtnDeactivate }} onClick={() => setActive(emp, false)}>Deactivate</button>}
                  </div>
                </div>
              </div>
            )
          })}
          {visibleEmployees.length === 0 && (
            <div style={styles.empty}>
              {filter === 'inactive' ? 'No inactive employees.' : filter === 'active' ? 'No active team members yet. Send them the signup link above!' : 'No team members yet. Send them the signup link above!'}
            </div>
          )}
        </div>
      )}

      {resetTarget && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Password Help</h2>
            <p style={styles.modalSub}>For <strong>{resetTarget.full_name}</strong></p>
            <div style={styles.infoBox}>
              <p style={styles.infoText}>
                There's no admin password reset yet — have them go to <strong>Settings → Change Password</strong> in the app if they're still logged in, or send them the signup link to create a fresh account:
              </p>
              <div style={styles.inviteUrl}>{inviteLink}</div>
              <button style={styles.copyBtnSmall} onClick={() => { navigator.clipboard.writeText(inviteLink); alert('Link copied!') }}>Copy Link</button>
            </div>
            <div style={styles.modalActions}>
              <button style={styles.cancelBtn} onClick={() => setResetTarget(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const styles = {
  page: { width: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' },
  title: { margin: 0, fontSize: '28px', fontWeight: '700', color: '#1a2e1a' },
  sub: { margin: '4px 0 0', color: '#6b7c6b', fontSize: '14px' },
  addBtn: { background: '#2d4a2d', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  filterBar: { display: 'flex', gap: '8px', marginBottom: '16px' },
  filterBtn: { background: '#f3f4f6', color: '#6b7c6b', border: '1px solid #e5e7eb', padding: '6px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  filterBtnActive: { background: '#2d4a2d', color: 'white', border: '1px solid #2d4a2d', padding: '6px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  inactiveBadge: { background: '#f3f4f6', color: '#6b7280', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' },
  inviteBox: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: '10px', padding: '16px 20px', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' },
  inviteLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  inviteIcon: { fontSize: '24px' },
  inviteTitle: { fontWeight: '700', color: '#1a2e1a', fontSize: '13px', marginBottom: '2px' },
  inviteUrl: { fontSize: '13px', color: '#16a34a', fontFamily: 'monospace', marginTop: '6px' },
  copyBtn: { background: '#16a34a', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', flexShrink: 0 },
  copyBtnSmall: { background: '#16a34a', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', marginTop: '8px' },
  addCard: { background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '2px solid #2d4a2d' },
  cardTitle: { margin: '0 0 4px', fontSize: '18px', fontWeight: '700', color: '#1a2e1a' },
  cardSub: { margin: '0 0 20px', fontSize: '13px', color: '#6b7c6b' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '16px' },
  label: { display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  optional: { fontWeight: '400', color: '#9ca3af', textTransform: 'none', letterSpacing: '0', fontSize: '11px' },
  input: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box' },
  formActions: { display: 'flex', gap: '8px', justifyContent: 'flex-end' },
  cancelBtn: { background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  saveBtn: { background: '#2d4a2d', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  loading: { color: '#6b7c6b', padding: '40px 0', textAlign: 'center' },
  list: { display: 'flex', flexDirection: 'column', gap: '10px' },
  card: { display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 20px', background: 'white', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb', flexWrap: 'wrap' },
  avatar: { width: '48px', height: '48px', borderRadius: '50%', background: '#2d4a2d', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: '700', flexShrink: 0 },
  info: { flex: 1, minWidth: '200px' },
  nameRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' },
  name: { fontWeight: '700', color: '#1a2e1a', fontSize: '16px' },
  clockedIn: { background: '#dcfce7', color: '#16a34a', padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' },
  details: { display: 'flex', flexWrap: 'wrap', gap: '10px', fontSize: '13px', color: '#6b7c6b' },
  right: { display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' },
  rateRow: { display: 'flex', alignItems: 'center', gap: '6px' },
  rateLabel: { fontSize: '12px', color: '#6b7c6b', fontWeight: '600' },
  rateInput: { width: '70px', padding: '6px 8px', borderRadius: '6px', border: '1.5px solid #d1d5db', fontSize: '14px', fontFamily: 'inherit', textAlign: 'right' },
  roleRow: { display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' },
  roleBtn: { padding: '6px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  roleBtnActive: { background: '#2d4a2d', color: 'white' },
  roleBtnAdmin: { background: '#7c3aed', color: 'white' },
  roleBtnInactive: { background: '#f3f4f6', color: '#9ca3af' },
  roleBtnReset: { background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' },
  roleBtnDeactivate: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' },
  roleBtnReactivate: { background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' },
  empty: { textAlign: 'center', padding: '40px 0', color: '#6b7c6b' },
  error: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '12px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' },
  modal: { background: 'white', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '440px' },
  modalTitle: { margin: '0 0 4px', fontSize: '20px', fontWeight: '700', color: '#1a2e1a' },
  modalSub: { margin: '0 0 16px', fontSize: '14px', color: '#6b7c6b' },
  modalActions: { display: 'flex', gap: '10px', marginTop: '20px' },
  infoBox: { background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px' },
  infoText: { margin: '0 0 4px', fontSize: '13px', color: '#374151', lineHeight: '1.6' },
}